import {
    styled,
    TableCell,
    TableRow,
    Table,
    TableContainer,
    TableBody,
    Box,
    Container,
    Typography,
    Grid
} from '@mui/material';
import { CaseDetailsModel } from '../../lib/models/CaseDetailsModel';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useScreenSmallerThan } from '../../lib/hooks/breakpoint-hooks';

export type CaseDetailsGeneralInfoProps = {
    isReadOnly: boolean | undefined;
    isDialogOpen: boolean;
    data: CaseDetailsModel;
};

const CaseDetailsGeneralInfo = ({
    data,
    isReadOnly,
    isDialogOpen
}: CaseDetailsGeneralInfoProps) => {
    const isSmallerThanSmall = useScreenSmallerThan('sm');
    const isSmallerThanMedium = useScreenSmallerThan('md');

    const TableDataCell = styled(TableCell)({
        border: 'none',
        paddingLeft: '0.625rem',
        paddingBottom: 0
    });

    const TypographyCell = styled(Typography)({
        fontWeight: 'bold',
        display: 'inline'
    });

    return (
        <>
            <Container maxWidth={false} sx={{ marginTop: '1.875rem' }}>
                <Grid container justifyContent="space-between">
                    <Grid item xs={isSmallerThanSmall ? 12 : 6}>
                        <Box sx={{ display: 'flex' }}>
                            <Box sx={{ fontWeight: 'bold', display: 'inline-flex' }}>
                                {data?.memberName}
                            </Box>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    paddingLeft: '0.938rem',
                                    fontSize: 13,
                                    lineHeight: '1.5rem'
                                }}>
                                {data?.memberAgeAtSurgery} Years Old{' '}
                                {data?.memberBmi != null ? '|' + data?.memberBmi + ' BMI ' : ''}
                            </Box>
                        </Box>
                        {data?.diagnosis?.map(function (item, i) {
                            return (
                                <Box sx={{ display: 'flex', fontSize: 13 }} key={i}>
                                    {item}
                                </Box>
                            );
                        })}
                    </Grid>
                    <Grid item xs={isSmallerThanSmall ? 12 : 6}>
                        <Box
                            sx={{
                                display: 'flex',
                                marginTop: isSmallerThanSmall ? '1rem' : 0,
                                justifyContent: isSmallerThanSmall ? 'flex-start' : 'flex-end'
                            }}></Box>
                    </Grid>
                </Grid>
            </Container>
            <TableContainer
                sx={
                    !isSmallerThanSmall
                        ? { padding: '1.25rem' }
                        : { padding: 0, margin: '0.875rem 0' }
                }>
                <Table
                    sx={{
                        background: 'white',
                        borderRadius: isSmallerThanSmall ? '0' : '0.625rem'
                    }}>
                    {!isSmallerThanSmall && !isSmallerThanMedium ? (
                        <TableBody>
                            <TableRow>
                                <TableDataCell>
                                    <TypographyCell>Case Stage: </TypographyCell>
                                    {data?.caseStage}
                                </TableDataCell>
                                <TableDataCell>
                                    <TypographyCell>LOB: </TypographyCell>
                                    {data?.lob}
                                </TableDataCell>
                                <TableDataCell>
                                    <TypographyCell>Date of Service: </TypographyCell>
                                    {data?.startDateOfService
                                        ? `${new Date(
                                              data?.startDateOfService
                                          ).toLocaleDateString()}`
                                        : ''}
                                </TableDataCell>
                            </TableRow>
                            <TableRow>
                                <TableDataCell>
                                    <TypographyCell>Review Type: </TypographyCell>
                                    {data?.reviewType}
                                </TableDataCell>
                                <TableDataCell>
                                    <TypographyCell>Client: </TypographyCell>
                                    {data?.payer}
                                </TableDataCell>
                                <TableDataCell>
                                    <TypographyCell>Facility Setting: </TypographyCell>
                                    {data?.facilitySetting}
                                </TableDataCell>
                            </TableRow>
                            <TableRow>
                                <TableDataCell sx={{ paddingBottom: '0.938rem' }}>
                                    <TypographyCell>Clinical Service: </TypographyCell>
                                    {data?.clinicalService}
                                </TableDataCell>
                                <TableDataCell sx={{ paddingBottom: '0.938rem' }}>
                                    <TypographyCell>Requesting Provider: </TypographyCell>
                                    {data?.requestingProvider}
                                    {data?.providerFlagged === true ? (
                                        <ErrorOutlineIcon
                                            sx={{
                                                color: 'red',
                                                verticalAlign: 'bottom',
                                                marginLeft: '0.313rem'
                                            }}></ErrorOutlineIcon>
                                    ) : (
                                        ''
                                    )}
                                </TableDataCell>
                                <TableDataCell sx={{ paddingBottom: '0.938rem' }}>
                                    <TypographyCell>Flagged Reasoning: </TypographyCell>
                                    {data?.providerFlaggedReason}
                                </TableDataCell>
                            </TableRow>
                        </TableBody>
                    ) : !isSmallerThanSmall ? (
                        <TableBody>
                            <TableRow>
                                <TableDataCell>
                                    <TypographyCell>Case Stage: </TypographyCell>
                                    {data?.caseStage}
                                </TableDataCell>
                                <TableDataCell>
                                    <TypographyCell>LOB: </TypographyCell>
                                    {data?.lob}{' '}
                                </TableDataCell>
                            </TableRow>
                            <TableRow>
                                <TableDataCell>
                                    <TypographyCell>Review Type: </TypographyCell>
                                    {data?.reviewType}
                                </TableDataCell>
                                <TableDataCell>
                                    <TypographyCell>Client: </TypographyCell>
                                    {data?.payer}
                                </TableDataCell>
                            </TableRow>
                            <TableRow>
                                <TableDataCell>
                                    <TypographyCell>Clinical Service: </TypographyCell>
                                    {data?.clinicalService}
                                </TableDataCell>
                                <TableDataCell>
                                    <TypographyCell>Requesting Provider: </TypographyCell>
                                    {data?.requestingProvider}
                                    {data?.providerFlagged === true ? (
                                        <ErrorOutlineIcon
                                            sx={{
                                                color: 'red',
                                                verticalAlign: 'bottom',
                                                marginLeft: '0.313rem'
                                            }}></ErrorOutlineIcon>
                                    ) : (
                                        ''
                                    )}
                                </TableDataCell>
                            </TableRow>
                            <TableRow>
                                <TableDataCell>
                                    <TypographyCell>Date of Service: </TypographyCell>
                                    {data?.startDateOfService
                                        ? `${new Date(
                                              data?.startDateOfService
                                          ).toLocaleDateString()}`
                                        : ''}
                                </TableDataCell>
                                <TableDataCell>
                                    <TypographyCell>Flagged Reasoning: </TypographyCell>
                                    {data?.providerFlaggedReason}
                                </TableDataCell>
                            </TableRow>
                            <TableRow>
                                <TableDataCell sx={{ paddingBottom: '0.625rem' }}>
                                    <TypographyCell>Facility Setting: </TypographyCell>
                                    {data?.facilitySetting}
                                </TableDataCell>
                            </TableRow>
                        </TableBody>
                    ) : (
                        <TableBody>
                            <TableRow>
                                <TableDataCell>
                                    <TypographyCell>Case Stage: </TypographyCell>
                                    {data?.caseStage}
                                </TableDataCell>
                            </TableRow>
                            <TableRow>
                                <TableDataCell>
                                    <TypographyCell>Review Type: </TypographyCell>
                                    {data?.reviewType}
                                </TableDataCell>
                            </TableRow>
                            <TableRow>
                                <TableDataCell>
                                    <TypographyCell>Clinical Service: </TypographyCell>
                                    {data?.clinicalService}
                                </TableDataCell>
                            </TableRow>
                            <TableRow>
                                <TableDataCell>
                                    <TypographyCell>LOB: </TypographyCell>
                                    {data?.lob}{' '}
                                </TableDataCell>
                            </TableRow>
                            <TableRow>
                                <TableDataCell>
                                    <TypographyCell>Client: </TypographyCell>
                                    {data?.payer}
                                </TableDataCell>
                            </TableRow>
                            <TableRow>
                                <TableDataCell>
                                    <TypographyCell>Requesting Provider: </TypographyCell>
                                    {data?.requestingProvider}
                                    {data?.providerFlagged === true ? (
                                        <ErrorOutlineIcon
                                            sx={{
                                                color: 'red',
                                                verticalAlign: 'bottom',
                                                marginLeft: '0.313rem'
                                            }}></ErrorOutlineIcon>
                                    ) : (
                                        ''
                                    )}
                                </TableDataCell>
                            </TableRow>
                            <TableRow>
                                <TableDataCell>
                                    <TypographyCell>Date of Service: </TypographyCell>
                                    {data?.startDateOfService
                                        ? `${new Date(
                                              data?.startDateOfService
                                          ).toLocaleDateString()}`
                                        : ''}
                                </TableDataCell>
                            </TableRow>
                            <TableRow>
                                <TableDataCell>
                                    <TypographyCell>Facility Setting: </TypographyCell>
                                    {data?.facilitySetting}
                                </TableDataCell>
                            </TableRow>
                            <TableRow>
                                <TableDataCell sx={{ paddingBottom: '0.625rem' }}>
                                    <TypographyCell>Flagged Reasoning: </TypographyCell>
                                    {data?.providerFlaggedReason}
                                </TableDataCell>
                            </TableRow>
                        </TableBody>
                    )}
                </Table>
            </TableContainer>
        </>
    );
};

export default CaseDetailsGeneralInfo;
